import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Row, Col, Tag, Empty, Select, DatePicker, Button, Space, Tooltip, Tabs,
  Table, Alert, Input,
} from 'antd';
import {
  TrophyOutlined, RocketOutlined, AlertOutlined, ReloadOutlined,
  TeamOutlined, RiseOutlined, FallOutlined, FireOutlined,
  CheckCircleOutlined, MailOutlined, ArrowLeftOutlined, BarChartOutlined,
  SearchOutlined, PauseCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { formatDate, formatMontant } from '../../variables/constants';
import TableExportButton from '../../components/TableExportButton';
import KpiCards from '../../components/KpiCards';
import PageHeader from '../../components/PageHeader';
import './performance.css';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

const GREEN = '#1a6b3c';

const { RangePicker } = DatePicker;

/* ─── Échelle de score (palette charte, dégressive) ───────────────── */
const couleurScore = (score) => {
  if (score >= 80) return { color: '#1a6b3c', bg: '#e8f5ed', label: i18n.t('performance:score.excellent') };
  if (score >= 60) return { color: '#0e7490', bg: '#e0f7fa', label: i18n.t('performance:score.bon') };
  if (score >= 40) return { color: '#a16207', bg: '#fef9c3', label: i18n.t('performance:score.aAmeliorer') };
  return { color: '#b91c1c', bg: '#fee2e2', label: i18n.t('performance:score.critique') };
};

/* Couleurs profils - tons institutionnels (vert / brun / sarcelle / or / ardoise) */
const PROFILS_COLOR = {
  REDACTEUR: '#1a6b3c',
  CSS: '#9b563a',
  SE: '#0e7490',
  SECRETAIRE: '#a16207',
  ADMIN: '#475569',
};

const tonProgress = (v, hauts = [70, 50]) =>
  v >= hauts[0] ? '#1a6b3c' : v >= hauts[1] ? '#a16207' : '#b91c1c';

const initialesFor = (nom) =>
  (nom || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();

/* ─── Anneau de score - élément signature, réutilisé partout ──────── */
function ScoreRing({ score, size = 56, valueSize = 17, showLabel = true }) {
  const sc = couleurScore(score);
  return (
    <div
      className="perf-ring"
      style={{ width: size, height: size, background: `conic-gradient(${sc.color} ${score * 3.6}deg, var(--cb-border-light) 0deg)` }}
    >
      <div className="perf-ring__inner">
        <span className="perf-ring__val perf-num" style={{ fontSize: valueSize, color: sc.color }}>{score}</span>
        {showLabel && <span className="perf-ring__cap">/ 100</span>}
      </div>
    </div>
  );
}

/* ─── Barre de progression maison ─────────────────────────────────── */
function Jauge({ label, value, hauts }) {
  const col = tonProgress(value, hauts);
  return (
    <div>
      <div className="perf-bar__head">
        <span className="lbl">{label}</span>
        <span className="val perf-num">{value}%</span>
      </div>
      <div className="perf-track">
        <div className="perf-track__fill" style={{ width: `${Math.min(100, value)}%`, background: col }} />
      </div>
    </div>
  );
}

/* ─── Carte employé (vue équipe) ──────────────────────────────────── */
function EmployeCard({ data, onClick }) {
  const { t } = useTranslation('performance');
  const profilCol = PROFILS_COLOR[data.profilNom] || '#6b7280';

  return (
    <div
      className="perf-emp"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    >
      {/* Header : identité + anneau de score */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div className="perf-avatar" style={{ background: `linear-gradient(135deg, ${profilCol}, ${profilCol}cc)` }}>
          {initialesFor(data.nomComplet)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {data.nomComplet}
          </div>
          <Tag style={{ marginTop: 4, borderRadius: 12, fontSize: 10, color: profilCol, borderColor: profilCol, background: '#fff' }}>
            {data.profilNom}
          </Tag>
        </div>
        <Tooltip title={t('carte.scoreAide')}>
          <span><ScoreRing score={data.scoreGlobal} /></span>
        </Tooltip>
      </div>

      {/* Bande de statistiques inline (sans encadrés) */}
      <div className="perf-stats">
        <div>
          <div className="perf-stat__v perf-num">{data.volumeTraite}</div>
          <div className="perf-stat__l">{t('carte.traites')}</div>
        </div>
        <div>
          <div className="perf-stat__v perf-num" style={{ color: GREEN }}>{data.chargeActuelle}</div>
          <div className="perf-stat__l">{t('carte.enCours')}</div>
        </div>
        <div>
          <Tooltip title={t('carte.traitementAide')}>
            <div className="perf-stat__v perf-num" style={{ color: '#0e7490' }}>
              {data.delaiMoyenTraitementJours ? t('carte.jours', { n: data.delaiMoyenTraitementJours }) : '-'}
            </div>
          </Tooltip>
          <div className="perf-stat__l">{t('carte.traitement')}</div>
        </div>
        <div>
          <Tooltip title={t('carte.relancesEmisesAide')}>
            <div className="perf-stat__v perf-num" style={{ color: GREEN }}>{data.relancesEmises ?? 0}</div>
          </Tooltip>
          <div className="perf-stat__l">{t('carte.relances')}</div>
        </div>
      </div>

      {/* Dossiers finis de son côté, mais bloqués par quelqu'un d'autre. Affiché
          à part de « En cours » : ce ne sont pas des dossiers en souffrance, et
          c'est toute la raison d'être de la notion d'attente. */}
      {data.dossiersEnAttenteTiers > 0 && (
        <div style={{
          margin: '0 0 12px', padding: '6px 10px', borderRadius: 8,
          background: '#fffbeb', color: '#78350f', fontSize: 11.5,
        }}>
          <PauseCircleOutlined style={{ marginRight: 6 }} />
          {t('carte.enAttenteTiers', { count: data.dossiersEnAttenteTiers })}
        </div>
      )}

      {/* Jauges */}
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        <Jauge label={t('carte.respectDelais')} value={data.tauxRespectDelais} />
        <Jauge label={t('carte.qualite')} value={data.scoreQualite} hauts={[80, 60]} />
      </Space>

      {/* Footer : relances + pénalités (affichés seulement si pertinents) */}
      {(data.totalRelancesRecues > 0 || Number(data.montantPenalites) > 0) && (
        <div style={{
          marginTop: 'auto', paddingTop: 14, borderTop: '1px solid var(--cb-border-light)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11.5
        }}>
          <Tooltip title={t('carte.relancesRecuesAide')}>
            <span style={{ color: data.totalRelancesRecues > 5 ? '#b91c1c' : '#6b7280' }}>
              <MailOutlined /> <span className="perf-num">{data.totalRelancesRecues}</span> {t('carte.motRelance', { count: data.totalRelancesRecues })}
            </span>
          </Tooltip>
          {Number(data.montantPenalites) > 0 && (
            <Tooltip title={t('carte.penalitesAide')}>
              <Tag color="red" style={{ borderRadius: 12, fontSize: 10, margin: 0 }}>
                <FireOutlined /> {formatMontant(data.montantPenalites)}
              </Tag>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Bandeau KPIs équipe ─────────────────────────────────────────── */
function HeaderKpis({ data }) {
  const { t } = useTranslation('performance');
  if (!data || data.length === 0) return null;
  const totalVolume = data.reduce((s, d) => s + d.volumeTraite, 0);
  const totalCharge = data.reduce((s, d) => s + d.chargeActuelle, 0);
  const moyTauxDelai = Math.round(data.reduce((s, d) => s + d.tauxRespectDelais, 0) / data.length);
  const moyScore = Math.round(data.reduce((s, d) => s + d.scoreGlobal, 0) / data.length);
  const sc = couleurScore(moyScore);

  const items = [
    { label: t('kpi.employesActifs'), value: data.length, icon: <TeamOutlined />, color: '#0e7490', bg: '#e0f7fa' },
    { label: t('kpi.sinistresTraites'), value: totalVolume, icon: <RocketOutlined />, color: GREEN, bg: '#e8f5ed' },
    { label: t('kpi.delaisRespectes'), value: `${moyTauxDelai}%`, icon: <CheckCircleOutlined />, color: moyTauxDelai >= 70 ? '#1a6b3c' : '#a16207', bg: moyTauxDelai >= 70 ? '#e8f5ed' : '#fef9c3' },
    { label: t('kpi.scoreEquipe', { niveau: sc.label }), value: `${moyScore} / 100`, icon: <TrophyOutlined />, color: sc.color, bg: sc.bg },
  ];

  return <KpiCards items={items} columns={4} />;
}

/* ─── Ligne benchmark : valeur employé vs repère équipe ───────────── */
function ComparaisonRow({ label, valeur, moyenne, suffix = '', inverser = false }) {
  const { t } = useTranslation('performance');
  const v = Number(valeur) || 0;
  const m = Number(moyenne) || 0;
  const diff = v - m;
  const egal = diff === 0;
  const meilleur = inverser ? diff < 0 : diff > 0;
  const col = egal ? '#9ca3af' : meilleur ? '#1a6b3c' : '#b91c1c';
  const scale = Math.max(v, m, 1) * 1.15;
  return (
    <div className="perf-bm">
      <div className="perf-bm__top">
        <span className="perf-bm__lbl">{label}</span>
        <span className="perf-bm__val perf-num">
          {v}{suffix}
          {!egal && (
            <Tag color={meilleur ? 'success' : 'error'} style={{ borderRadius: 10, margin: 0, fontSize: 10 }}>
              {meilleur ? <RiseOutlined /> : <FallOutlined />} {diff > 0 ? '+' : ''}{diff}{suffix}
            </Tag>
          )}
        </span>
      </div>
      <div className="perf-bm__bar">
        <div className="perf-bm__fill" style={{ width: `${Math.min(100, (v / scale) * 100)}%`, background: col }} />
        <span className="perf-bm__marker" style={{ left: `${Math.min(100, (m / scale) * 100)}%` }} />
      </div>
      <div className="perf-bm__cap">{t('detail.repereEquipe')} <span className="perf-num">{m}{suffix}</span></div>
    </div>
  );
}

/* ─── Vue détail employé (drill-down) ─────────────────────────────── */
function DetailEmploye({ userId, dateDebut, dateFin, onBack }) {
  const navigate = useNavigate();
  const { t } = useTranslation('performance');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dateDebut) params.append('dateDebut', dateDebut.format('YYYY-MM-DD'));
    if (dateFin) params.append('dateFin', dateFin.format('YYYY-MM-DD'));
    axiosInstance.get(`/v1/stats/performance-employes/${userId}?${params}`)
      .then(r => setData(r.data?.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [userId, dateDebut, dateFin]);

  if (loading) return <div className="cb-loader">{t('chargement')}</div>;
  if (!data) return <Empty description={t('detail.aucuneDonnee')} />;

  const { kpis, evolution, dossiersEnRetard, moyenneEquipe } = data;
  const score = couleurScore(kpis.scoreGlobal);
  const maxVol = Math.max(1, ...evolution.map(e => Math.max(e.volumeTraite, e.delaisResolus + e.delaisDepasses)));

  return (
    <>
      <Button icon={<ArrowLeftOutlined />} onClick={onBack} style={{ marginBottom: 16 }}>
        {t('detail.retour')}
      </Button>

      {/* Bandeau identité */}
      <div className="cb-card" style={{
        marginBottom: 16, padding: '20px 24px', border: 'none',
        background: 'linear-gradient(135deg, var(--cb-green), var(--cb-green-dark))',
      }}>
        <Row gutter={16} align="middle">
          <Col>
            <div className="perf-avatar" style={{
              width: 64, height: 64, fontSize: 24, borderRadius: 16,
              background: 'rgba(255,255,255,0.18)', color: '#fff',
            }}>
              {initialesFor(kpis.nomComplet)}
            </div>
          </Col>
          <Col flex="auto">
            <div style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>{kpis.nomComplet}</div>
            <div style={{ marginTop: 4, color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>
              {kpis.email}
              <Tag style={{ marginLeft: 8, borderRadius: 12, color: GREEN, borderColor: 'transparent', background: '#fff' }}>
                {kpis.profilNom}
              </Tag>
            </div>
          </Col>
          <Col>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <ScoreRing score={kpis.scoreGlobal} size={72} valueSize={24} />
              <div>
                <div className="perf-eyebrow" style={{ color: 'rgba(255,255,255,0.7)' }}>{t('detail.scoreGlobal')}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{score.label}</div>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <Row gutter={16}>
        {/* Colonne gauche : KPIs détaillés */}
        <Col xs={24} lg={10}>
          <div className="cb-card" style={{ marginBottom: 16 }}>
            <div className="cb-card-header"><h3 className="cb-card-title"><BarChartOutlined /> {t('detail.comparaison')}</h3></div>
            <ComparaisonRow label={t('detail.lignes.sinistresTraites')} valeur={kpis.volumeTraite} moyenne={moyenneEquipe.volumeTraite} />
            <ComparaisonRow label={t('detail.lignes.chargeActuelle')} valeur={kpis.chargeActuelle} moyenne={moyenneEquipe.chargeActuelle} />
            <ComparaisonRow label={t('detail.lignes.respectDelais')} valeur={kpis.tauxRespectDelais} moyenne={moyenneEquipe.tauxRespectDelais} suffix="%" />
            <ComparaisonRow label={t('detail.lignes.tauxContentieux')} valeur={kpis.tauxContentieux} moyenne={moyenneEquipe.tauxContentieux} suffix="%" inverser />
            <ComparaisonRow label={t('detail.lignes.vitesseMoyenne')} valeur={kpis.vitesseMoyenneJours} moyenne={moyenneEquipe.vitesseMoyenneJours} suffix={t('detail.suffixeJours')} inverser />
            <ComparaisonRow label={t('detail.lignes.relancesRecues')} valeur={kpis.totalRelancesRecues} moyenne={moyenneEquipe.totalRelancesRecues} inverser />
            <ComparaisonRow label={t('detail.lignes.relancesEnvoyees')} valeur={kpis.relancesEmises} moyenne={moyenneEquipe.relancesEmises} />
            <ComparaisonRow label={t('detail.lignes.dossiersConfies')} valeur={kpis.dossiersAffectes} moyenne={moyenneEquipe.dossiersAffectes} />
            <ComparaisonRow label={t('detail.lignes.prisEnCharge')} valeur={kpis.dossiersPrisEnCharge} moyenne={moyenneEquipe.dossiersPrisEnCharge} />
            <ComparaisonRow label={t('detail.lignes.clotures')} valeur={kpis.dossiersClotures} moyenne={moyenneEquipe.dossiersClotures} />
            <ComparaisonRow label={t('detail.lignes.enAttenteTiers')} valeur={kpis.dossiersEnAttenteTiers} moyenne={moyenneEquipe.dossiersEnAttenteTiers} />
            <ComparaisonRow label={t('detail.lignes.delaiTraitement')} valeur={kpis.delaiMoyenTraitementJours} moyenne={moyenneEquipe.delaiMoyenTraitementJours} suffix={t('detail.suffixeJours')} inverser />
            <div className="perf-cmp">
              <span className="perf-cmp__lbl">{t('detail.lignes.penalites')}</span>
              <Space size={10}>
                <Tag color={Number(kpis.montantPenalites) > 0 ? 'error' : 'success'} style={{ borderRadius: 10, fontSize: 12, margin: 0 }}>
                  {formatMontant(kpis.montantPenalites)}
                </Tag>
                <span className="perf-cmp__avg perf-num">{t('detail.moyenneEquipe', { montant: formatMontant(moyenneEquipe.montantPenalites) })}</span>
              </Space>
            </div>
          </div>
        </Col>

        {/* Colonne droite : évolution + dossiers en retard */}
        <Col xs={24} lg={14}>
          <div className="cb-card" style={{ marginBottom: 16 }}>
            <div className="cb-card-header"><h3 className="cb-card-title">{t('detail.evolution')}</h3></div>
            <div className="perf-evo">
              {evolution.map((m, i) => (
                <div key={i} className="perf-evo__col">
                  <div className="perf-evo__bars">
                    <Tooltip title={t('detail.volumeTraite', { n: m.volumeTraite })}>
                      <div className="perf-evo__bar" style={{ height: (m.volumeTraite / maxVol) * 130, background: '#0e7490' }} />
                    </Tooltip>
                    <Tooltip title={t('detail.delaisRespectes', { n: m.delaisResolus })}>
                      <div className="perf-evo__bar" style={{ height: (m.delaisResolus / maxVol) * 130, background: '#1a6b3c' }} />
                    </Tooltip>
                    <Tooltip title={t('detail.delaisDepasses', { n: m.delaisDepasses })}>
                      <div className="perf-evo__bar" style={{ height: (m.delaisDepasses / maxVol) * 130, background: '#dc2626' }} />
                    </Tooltip>
                  </div>
                  <div className="perf-evo__lbl">{m.label}</div>
                </div>
              ))}
            </div>
            <div className="perf-evo__legend">
              <span><span className="perf-evo__dot" style={{ background: '#0e7490' }} />{t('detail.legende.volume')}</span>
              <span><span className="perf-evo__dot" style={{ background: '#1a6b3c' }} />{t('detail.legende.delaisOk')}</span>
              <span><span className="perf-evo__dot" style={{ background: '#dc2626' }} />{t('detail.legende.delaisKo')}</span>
            </div>
          </div>

          <div className="cb-card" style={{ padding: dossiersEnRetard.length === 0 ? 24 : '24px 24px 8px' }}>
            <div className="cb-card-header">
              <h3 className="cb-card-title"><AlertOutlined style={{ color: '#dc2626' }} /> {t('detail.aSurveiller', { n: dossiersEnRetard.length })}</h3>
            </div>
            {dossiersEnRetard.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 13, padding: '8px 0' }}>
                <CheckCircleOutlined style={{ color: '#1a6b3c', marginRight: 6 }} />
                {t('detail.aucunRetard')}
              </div>
            ) : (
              <Table className="cb-table" size="small" pagination={false}
                rowKey={(r) => r.sinistreTrackingId + r.libelleDelai}
                dataSource={dossiersEnRetard}
                columns={[
                  {
                    title: t('detail.colonnes.sinistre'), dataIndex: 'numeroSinistre', width: 130,
                    render: (v, r) => (
                      <button onClick={() => navigate(`/sinistres/${r.sinistreTrackingId}`)}
                        style={{ background: 'none', border: 'none', color: GREEN, fontWeight: 700, cursor: 'pointer', padding: 0, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                        {v}
                      </button>
                    )
                  },
                  {
                    title: t('detail.colonnes.delai'), dataIndex: 'libelleDelai', ellipsis: true,
                    render: (v) => <span style={{ fontSize: 12 }}>{v}</span>
                  },
                  {
                    title: t('detail.colonnes.niveau'), dataIndex: 'niveauAlerte', width: 100,
                    render: (v) => {
                      const map = { CRITIQUE: 'error', URGENT: 'warning', ATTENTION: 'gold' };
                      return <Tag color={map[v] || 'default'} style={{ borderRadius: 10, fontSize: 11 }}>{v}</Tag>;
                    }
                  },
                  { title: t('detail.colonnes.echeance'), dataIndex: 'dateEcheance', width: 100, render: (v) => <span style={{ fontSize: 11 }}>{formatDate(v)}</span> },
                  {
                    title: t('detail.colonnes.retard'), dataIndex: 'joursDepassement', width: 80, align: 'center',
                    render: (v) => v > 0
                      ? <Tag color="red" style={{ borderRadius: 10, fontSize: 11 }} className="perf-num">{t('detail.retardJours', { n: v })}</Tag>
                      : <Tag style={{ borderRadius: 10, fontSize: 11 }}>OK</Tag>
                  },
                ]}
              />
            )}
          </div>
        </Col>
      </Row>
    </>
  );
}

/* ─── Vue équipe (cards) ──────────────────────────────────────────── */
function VueEquipeTab({ data, loading, onRefresh, onSelect }) {
  const { t } = useTranslation('performance');
  const [tri, setTri] = useState('score');

  const sortedData = useMemo(() => {
    const arr = [...data];
    if (tri === 'score') arr.sort((a, b) => b.scoreGlobal - a.scoreGlobal);
    else if (tri === 'volume') arr.sort((a, b) => b.volumeTraite - a.volumeTraite);
    else if (tri === 'charge') arr.sort((a, b) => b.chargeActuelle - a.chargeActuelle);
    else if (tri === 'delais') arr.sort((a, b) => b.tauxRespectDelais - a.tauxRespectDelais);
    return arr;
  }, [data, tri]);

  return (
    <>
      <Alert type="info" showIcon style={{ marginBottom: 16, borderRadius: 10 }}
        message={t('equipe.avertissement')}
      />

      <div className="perf-toolbar">
        <Select value={tri} onChange={setTri} style={{ width: 200 }}
          options={[
            { value: 'score', label: t('equipe.tri.score') },
            { value: 'volume', label: t('equipe.tri.volume') },
            { value: 'charge', label: t('equipe.tri.charge') },
            { value: 'delais', label: t('equipe.tri.delais') },
          ]} />
        <span className="perf-toolbar__spacer" />
        <Button icon={<ReloadOutlined />} onClick={onRefresh}>{t('actualiser')}</Button>
      </div>

      {loading ? (
        <div className="cb-loader" style={{ color: '#6b7280' }}>{t('chargement')}</div>
      ) : sortedData.length === 0 ? (
        <Empty description={t('equipe.aucunEmploye')} />
      ) : (
        <Row gutter={[16, 16]}>
          {sortedData.map(e => (
            <Col xs={24} sm={12} lg={8} xl={6} key={e.utilisateurTrackingId}>
              <EmployeCard data={e} onClick={() => onSelect(e)} />
            </Col>
          ))}
        </Row>
      )}
    </>
  );
}

/* ─── Vue tableau classement ──────────────────────────────────────── */
const RANG_STYLE = {
  0: { background: '#fdf6d8', color: '#a16207' }, // or
  1: { background: '#eef1f4', color: '#64748b' }, // argent
  2: { background: '#f6e9e0', color: '#9b563a' }, // bronze
};

function ClassementTab({ data, loading, onRefresh, dateDebut, dateFin, onSelect }) {
  const { t } = useTranslation('performance');
  const sorted = useMemo(
    () => [...data]
      .sort((a, b) => b.scoreGlobal - a.scoreGlobal)
      .map((r, i) => ({ ...r, _rang: i + 1 })),
    [data],
  );

  const cols = [
    {
      title: t('classement.colonnes.rang'), key: 'rang', width: 64, align: 'center',
      render: (_, r) => (
        <span className="perf-rank perf-num" style={RANG_STYLE[r._rang - 1] || { background: '#f4f6f5', color: '#6b7280' }}>
          {r._rang}
        </span>
      ),
      export: (_, r) => r._rang
    },
    {
      title: t('classement.colonnes.employe'), dataIndex: 'nomComplet',
      render: (v, r) => (
        <Space>
          <div className="perf-avatar" style={{ width: 34, height: 34, fontSize: 12, borderRadius: 10, background: PROFILS_COLOR[r.profilNom] || '#6b7280' }}>
            {initialesFor(v)}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{v}</div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>{r.profilNom}</div>
          </div>
        </Space>
      ),
      export: (v) => v
    },
    {
      title: t('classement.colonnes.score'), dataIndex: 'scoreGlobal', width: 100, align: 'center', numeric: true,
      render: (v) => {
        const sc = couleurScore(v);
        return (
          <span className="perf-num" style={{
            display: 'inline-block', minWidth: 52, padding: '3px 0', borderRadius: 12,
            fontWeight: 800, fontSize: 13, color: sc.color, background: sc.bg
          }}>
            {v}
          </span>
        );
      }
    },
    {
      title: t('classement.colonnes.volume'), dataIndex: 'volumeTraite', width: 90, align: 'center', numeric: true,
      render: (v) => <strong className="perf-num">{v}</strong>
    },
    {
      title: t('classement.colonnes.charge'), dataIndex: 'chargeActuelle', width: 90, align: 'center', numeric: true,
      render: (v) => <span className="perf-num" style={{ color: GREEN, fontWeight: 600 }}>{v}</span>
    },
    {
      title: t('classement.colonnes.delais'), dataIndex: 'tauxRespectDelais', width: 130, align: 'left',
      render: (v) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="perf-track" style={{ flex: 1 }}>
            <div className="perf-track__fill" style={{ width: `${Math.min(100, v)}%`, background: tonProgress(v) }} />
          </div>
          <span className="perf-num" style={{ fontSize: 11, color: '#6b7280', width: 30, textAlign: 'right' }}>{v}%</span>
        </div>
      ),
      export: (v) => `${v}%`
    },
    {
      title: t('classement.colonnes.qualite'), dataIndex: 'scoreQualite', width: 130, align: 'left',
      render: (v) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="perf-track" style={{ flex: 1 }}>
            <div className="perf-track__fill" style={{ width: `${Math.min(100, v)}%`, background: tonProgress(v, [80, 60]) }} />
          </div>
          <span className="perf-num" style={{ fontSize: 11, color: '#6b7280', width: 30, textAlign: 'right' }}>{v}%</span>
        </div>
      ),
      export: (v) => `${v}%`
    },
    {
      title: t('classement.colonnes.vitesse'), dataIndex: 'vitesseMoyenneJours', width: 90, align: 'center', numeric: true,
      render: (v) => v > 0 ? <span className="perf-num">{t('classement.jours', { n: v })}</span> : '-'
    },
    {
      title: t('classement.colonnes.relances'), dataIndex: 'totalRelancesRecues', width: 90, align: 'center', numeric: true,
      render: (v) => v > 5 ? <Tag color="red" className="perf-num">{v}</Tag> : v > 0 ? <span className="perf-num">{v}</span> : '-'
    },
    {
      title: t('classement.colonnes.penalites'), dataIndex: 'montantPenalites', width: 130, align: 'right', format: 'currency', numeric: true,
      render: (v) => Number(v) > 0
        ? <strong style={{ color: '#b91c1c' }}>{formatMontant(v)}</strong>
        : <span style={{ color: '#9ca3af' }}>-</span>
    },
    {
      title: '', key: 'act', width: 70,
      render: (_, r) => <Button size="small" type="text" style={{ color: GREEN }} onClick={() => onSelect(r)}>{t('classement.detail')}</Button>,
      exportable: false
    },
  ];

  return (
    <>
      <div className="perf-toolbar">
        <span className="perf-toolbar__spacer" />
        <Button icon={<ReloadOutlined />} onClick={onRefresh}>{t('actualiser')}</Button>
      </div>
      <div className="cb-card" style={{ padding: 0 }}>
        <Table className="cb-table" dataSource={sorted} columns={cols} rowKey="utilisateurTrackingId"
          loading={loading} scroll={{ x: 1200 }}
          pagination={{ pageSize: 25, showTotal: (total) => t('classement.totalEmployes', { count: total }) }}
          locale={{ emptyText: <Empty description={t('classement.aucuneDonnee')} /> }} />
      </div>

      <TableExportButton
        floating
        data={sorted}
        columns={cols}
        filename="performance-equipe"
        title={t('titre')}
        subtitle={t('classement.periode', { debut: dateDebut?.format('DD/MM/YYYY'), fin: dateFin?.format('DD/MM/YYYY') })}
        count={sorted.length}
      />
    </>
  );
}

/* ─── Page principale ─────────────────────────────────────────────── */
export default function Performance() {
  const { t } = useTranslation('performance');
  const [activeTab, setActiveTab] = useState('cards');
  const [dateRange, setDateRange] = useState([dayjs().subtract(3, 'month'), dayjs()]);
  const [selectedEmploye, setSelectedEmploye] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [profil, setProfil] = useState('');

  const dateDebut = dateRange?.[0];
  const dateFin = dateRange?.[1];

  const filteredData = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter(d =>
      (!profil || d.profilNom === profil) &&
      (!q || (d.nomComplet || '').toLowerCase().includes(q) || (d.email || '').toLowerCase().includes(q))
    );
  }, [data, search, profil]);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dateDebut) params.append('dateDebut', dateDebut.format('YYYY-MM-DD'));
    if (dateFin) params.append('dateFin', dateFin.format('YYYY-MM-DD'));
    axiosInstance.get(`/v1/stats/performance-employes?${params}`)
      .then(r => setData(r.data?.data || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [dateDebut, dateFin]);

  useEffect(() => { load(); }, [load]);

  if (selectedEmploye) {
    return (
      <Layout title={t('titreDetail')}>
        <DetailEmploye
          userId={selectedEmploye.utilisateurTrackingId}
          dateDebut={dateDebut}
          dateFin={dateFin}
          onBack={() => setSelectedEmploye(null)}
        />
      </Layout>
    );
  }

  return (
    <Layout title={t('titre')}>
      {/* `filteredData` et non `data` : le compte doit suivre les filtres de la
          barre d'outils, qui alimentent aussi les KPI et les deux onglets. */}
      <PageHeader
        titre={t('titre')}
        icone={<TeamOutlined />}
        compte={filteredData.length}
        unite={t('unite')}
        unitePluriel={t('unitePluriel')}
        actions={(
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            format="DD/MM/YYYY"
            presets={[
              { label: t('periodes.moisCourant'), value: [dayjs().startOf('month'), dayjs()] },
              { label: t('periodes.trimestre'), value: [dayjs().subtract(3, 'month'), dayjs()] },
              { label: t('periodes.annee'), value: [dayjs().startOf('year'), dayjs()] },
              { label: t('periodes.douzeMois'), value: [dayjs().subtract(12, 'month'), dayjs()] },
            ]}
          />
        )}
      />

      {/* KPIs équipe - au-dessus des onglets pour rester visibles sur les deux vues */}
      <HeaderKpis data={filteredData} />

      {/* Filtres de recherche - appliqués aux KPIs et aux deux onglets */}
      <div className="perf-toolbar">
        <Input allowClear prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
          placeholder={t('recherche')} style={{ width: 300 }}
          value={search} onChange={(e) => setSearch(e.target.value)} />
        <Select allowClear placeholder={t('tousProfils')} style={{ width: 180 }}
          value={profil || undefined} onChange={(v) => setProfil(v || '')}
          options={Object.keys(PROFILS_COLOR).map(p => ({ value: p, label: p }))} />
        {(search || profil) && (
          <Button type="link" style={{ color: GREEN, paddingLeft: 0 }}
            onClick={() => { setSearch(''); setProfil(''); }}>
            {t('reinitialiser')}
          </Button>
        )}
      </div>

      <div className="cb-card" style={{ padding: '0 20px' }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}
          items={[
            {
              key: 'cards', label: <><TeamOutlined /> {t('onglets.vueEquipe')}</>,
              children: <VueEquipeTab data={filteredData} loading={loading} onRefresh={load} onSelect={setSelectedEmploye} />
            },
            {
              key: 'classement', label: <><TrophyOutlined /> {t('onglets.classement')}</>,
              children: <ClassementTab data={filteredData} loading={loading} onRefresh={load} dateDebut={dateDebut} dateFin={dateFin} onSelect={setSelectedEmploye} />
            },
          ]}
        />
      </div>
    </Layout>
  );
}
